import React, { useState, useContext, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/HeaderB";
import ProfileSidebar from "../components/ProfileSideBar";
import { AuthContext } from "../context/AuthContext";
import { Upload, Camera, Pencil } from "lucide-react";
import { translateText } from "../utils/categoryTranslations";

//reusable component for the Form fields
const FieldRow = ({ label, children }) => (
  <div className="flex items-center gap-4 w-full">
    <span className="w-32 font-semibold text-custom-letters text-left">{label}:</span>
    <div className="flex-1">{children}</div>
  </div>
);

//main component
const EditDiscount = () => {
  const { id } = useParams(); //get the id from the URL
  const navigate = useNavigate(); //redirect for after save

  const { accessToken, language, userRole } = useContext(AuthContext);

  const [loading, setLoading] = useState(false); //loader

  const [categories, setCategories] = useState([]);
  const [displayData, setDisplayData] = useState({ storeName: "", road: "" });

  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [locations, setLocations] = useState([]);

  //URL for photo preview
  const [photoPreview, setPhotoPreview] = useState(null);
  //the photo file send to the back
  const [photoFile, setPhotoFile] = useState(null);

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errorPopup, setErrorPopup] = useState(null);

  const [showCategoryPopup, setShowCategoryPopup] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  const cameraInputRef = useRef(null);

  const translations = {
    el: {
      title: "Επεξεργασία στοιχείων προσφοράς",
      locSection: "Εισάγετε τοποθεσία προσφοράς",
      detailsSection: "Εισάγετε λεπτομέρειες προσφοράς",
      city: "Πόλη",
      area: "Περιοχή",
      store: "Κατάστημα",
      product: "Προϊόν",
      category: "Κατηγορία",
      origPrice: "Αρχική τιμή",
      discPrice: "Τιμή προσφοράς",
      discPercent: "Έκπτωση (%)",
      selectCity: "Επιλέξτε πόλη",
      selectArea: "Επιλέξτε περιοχή",
      selectStore: "Επιλέξτε κατάστημα",
      addNewStore: "Προσθήκη νέου καταστήματος",
      addNewCat: "Προσθήκη νέας κατηγορίας",
      newPhoto: "Νέα φωτογραφία",
      camera: "Κάμερα",
      saveBtn: "Αποθήκευση αλλαγών",
      loading: "Φόρτωση...",
      updated: "Ενημερώθηκε!",
      successMsg: "Τα στοιχεία του προϊόντος αποθηκεύτηκαν με επιτυχία.",
      done: "Έτοιμο",
      addCatTitle: "Προσθήκη νέας κατηγορίας",
      cancel: "Ακύρωση",
      save: "Αποθήκευση",
      placeholderCat: "π.χ. Γαλακτοκομικά",
      errorTitle: "Σφάλμα",
      errorSave: "Κάτι πήγε στραβά κατά την αποθήκευση.",
      ok: "Εντάξει"
    },
    en: {
      title: "Edit offer details",
      locSection: "Enter offer location",
      detailsSection: "Enter offer details",
      city: "City",
      area: "Area",
      store: "Store",
      product: "Product",
      category: "Category",
      origPrice: "Original price",
      discPrice: "Offer price",
      discPercent: "Discount (%)",
      selectCity: "Select city",
      selectArea: "Select area",
      selectStore: "Select store",
      addNewStore: "Add new store",
      addNewCat: "Add new category",
      newPhoto: "New photo",
      camera: "Camera",
      saveBtn: "Save changes",
      loading: "Loading...",
      updated: "Updated!",
      successMsg: "Product details saved successfully.",
      done: "Done",
      addCatTitle: "Add new category",
      cancel: "Cancel",
      save: "Save",
      placeholderCat: "e.g. Dairy",
      errorTitle: "Error",
      errorSave: "Something went wrong while saving.",
      ok: "OK"
    }
  };

  const t = translations[language] || translations.el;
  const editSuccessPath = userRole === "admin" ? "/admindiscounts" : "/mydiscounts";

  //form state
  const [form, setForm] = useState({
    title: "",
    category: "",
    originalPrice: "",
    discountPrice: "",
    discount: "",
    city: "",
    area: "",
    store: "",
  });

  const handleSimpleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [catsRes, citiesRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/editdiscount/categories`, { headers: { Authorization: `Bearer ${accessToken}` } }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/editdiscount/cities?lang=${language}`, { headers: { Authorization: `Bearer ${accessToken}` } })
        ]);
        setCategories(await catsRes.json());
        const citiesData = await citiesRes.json();
        setCities(citiesData.cities || []);
      } catch (err) { console.error(err); }
    };
    if (accessToken) fetchInitialData();
  }, [accessToken]);

  //Fetch Discount Details (Existing useEffect, slightly modified)
  useEffect(() => {
    if (!accessToken || !id) return;
    const fetchDiscount = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/editdiscount/${id}`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        const data = await res.json();
        const s = data.store_details;

        setForm({
          title: language === 'el' ? (data.title || "") : (data.title_en || ""),
          category: data.category_id || "",
          originalPrice: data.original_price || "",
          discountPrice: data.discount_price || "",
          discount: data.discount || "",
          city: language === 'el' ? s?.city_el : s?.city_en,
          area: language === 'el' ? s?.area_el : s?.area_en,
          store: data.store_id || "",
        });
        setPhotoPreview(data.photo_url);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchDiscount();
  }, [id, accessToken]);

  //Cascading Fetch for Areas (triggered when city value changes)
  useEffect(() => {
    if (!form.city) return;
    const fetchAreas = async () => {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/editdiscount/areas?city=${form.city}&lang=${language}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      const areasData = await res.json();
      setAreas(areasData.areas || []);
    };
    fetchAreas();
  }, [form.city, accessToken]);

  // 4. Cascading Fetch for Stores (triggered when area value changes)
  useEffect(() => {
    if (!form.city || !form.area) return;
    const fetchStores = async () => {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/editdiscount/stores?city=${form.city}&areas=${form.area}&lang=${language}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      const storesData = await res.json();
      setLocations(storesData.stores || []);
    };
    fetchStores();
  }, [form.city, form.area, accessToken]);

  const handleSaveCategory = async () => {
    if (!newCategoryName) return;

    // Determine translation direction based on current language preference
    const fromLang = language;
    const toLang = language === "en" ? "el" : "en";

    // Execute translation API call
    const translated = await translateText(newCategoryName, fromLang, toLang);

    // Prepare payload for database insertion
    // category_name always stores English, category_name_el always stores Greek
    const payload = {
      nameEn: language === "en" ? newCategoryName : translated,
      nameEl: language === "el" ? newCategoryName : translated
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/editdiscount/categories/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const json = await res.json();
        setCategories([...categories, json.category]);
        setForm({ ...form, category: json.category.category_id });
        setShowCategoryPopup(false);
        setNewCategoryName("");
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Updated HandleSubmit function for discount updates
  const handleSubmit = async () => {
    const payload = new FormData();
    payload.append("title", form.title);
    payload.append("category", form.category);
    payload.append("store", form.store);       
    payload.append("originalPrice", form.originalPrice);
    payload.append("discountPrice", form.discountPrice);
    payload.append("discount", form.discount);
    if (photoFile) payload.append("photo", photoFile);

    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/editdiscount/${id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: payload,
      });
      if (res.ok) setShowSuccessPopup(true);
    } catch (err) { setErrorPopup({ title: t.errorTitle, msg: t.errorSave }); }
    finally { setLoading(false); }
  };
  //photo handling
  const handlePhoto = (file) => {
    if (file) {
      setPhotoFile(file); //photo file for back
      setPhotoPreview(URL.createObjectURL(file)); //temp URL for preview
    }
  };

  // Automatic discount percentage calculation when prices change
  useEffect(() => {
    const original = parseFloat(form.originalPrice);
    const discounted = parseFloat(form.discountPrice);

    if (original > 0 && discounted >= 0 && discounted < original) {
      const calcDiscount = ((original - discounted) / original) * 100;
      // Update state with the calculated discount rounded to 2 decimal places
      setForm(prev => ({ ...prev, discount: calcDiscount.toFixed(2) }));
    } else {
      // If prices are invalid, clear the discount field
      setForm(prev => ({ ...prev, discount: "" }));
    }
  }, [form.originalPrice, form.discountPrice]);

  // if (loading) return <div className="min-h-screen flex items-center justify-center bg-custom-background text-custom-letters">{t.loading}</div>;

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />
      <div className="flex-1 flex flex-row relative bg-custom-background">
        <ProfileSidebar />
        <main id="editdiscount-content" className="flex flex-col flex-1 items-center justify-start px-4 md:px-10 py-10 pt-20 md:pt-10 xl:ml-20 text-center md:text-left">
          <div className="w-full max-w-3xl mx-auto bg-custom-header rounded-3xl shadow-xl p-8 md:p-12 text-custom-letters">
            <div className="flex flex-col gap-6">
              <h2 className="text-lg md:text-2xl font-semibold mb-8 text-custom-letters text-center flex justify-center items-center gap-2">
                <Pencil className="text-custom-grad1" /> {t.title}
              </h2>

              <p className="text-lg md:text-xl font-medium mt-4 text-custom-letters text-center border-b border-custom-grad1/20 pb-2">
                {t.locSection}
              </p>
              {/* Read only location info */}
              <FieldRow label={t.city}>
                <select name="city" value={form.city} onChange={handleSimpleChange} className="w-full bg-custom-background px-4 py-2 rounded-full text-sm">
                  <option value="">{t.selectCity}</option>
                  {cities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </FieldRow>

              <FieldRow label={t.area}>
                <select name="area" value={form.area} onChange={handleSimpleChange} disabled={!form.city} className="w-full bg-custom-background px-4 py-2 rounded-full text-sm">
                  <option value="">{t.selectArea}</option>
                  {areas.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </FieldRow>

              <FieldRow label={t.store}>
                <select name="store" value={form.store}
                  onChange={(e) => {
                    if (e.target.value === "ADD_NEW_STORE") {
                      navigate("/addstore");
                    } else {
                      handleSimpleChange(e);
                    }
                  }} disabled={!form.area} className="w-full bg-custom-background px-4 py-2 rounded-full text-sm">
                  <option value="">{t.selectStore}</option>
                  {locations.map(s => (
                    <option key={s.store_id} value={s.store_id}>
                      {s.store_name} ({s.area} - {s.road})
                    </option>
                  ))}
                  <option disabled>──────────</option>
                  <option value="ADD_NEW_STORE" className="font-bold text-custom-grad1">
                    ➕ {t.addNewStore}
                  </option>
                </select>
              </FieldRow>

              <p className="text-lg md:text-xl font-medium mt-8 text-custom-letters text-center border-b border-custom-grad1/20 pb-2">
                {t.detailsSection}
              </p>

              <FieldRow label={t.product}>
                <input name="title" value={form.title} onChange={handleSimpleChange} className="w-full bg-custom-background px-4 py-2 rounded-full outline-none focus:ring-2 focus:ring-custom-grad1" />
              </FieldRow>

              {/*Categories Select*/}
              <FieldRow label={t.category}>
                <select
                  name="category"
                  value={form.category}
                  onChange={(e) => {
                    if (e.target.value === "ADD_NEW_CAT") {
                      setShowCategoryPopup(true);
                    } else {
                      handleSimpleChange(e);
                    }
                  }}
                  className="w-full bg-custom-background px-4 py-2 rounded-full text-sm"
                > {/*loops through the array to create all the options*/}
                  {categories.map((cat) => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {language === 'el' ? cat.category_name_el : cat.category_name}
                    </option>
                  ))}
                  <option disabled>──────────</option>
                  <option value="ADD_NEW_CAT" className="font-bold text-custom-grad1">
                    ➕ {t.addNewCat}
                  </option>
                </select>
              </FieldRow>

              <FieldRow label={t.origPrice}>
                <input name="originalPrice" type="number" value={form.originalPrice} onChange={handleSimpleChange} className="w-full bg-custom-background px-4 py-2 rounded-full" />
              </FieldRow>

              <FieldRow label={t.discPrice}>
                <input name="discountPrice" type="number" value={form.discountPrice} onChange={handleSimpleChange} className="w-full bg-custom-background px-4 py-2 rounded-full" />
              </FieldRow>

              <FieldRow label={t.discPercent}>
                <input name="discount" type="number" value={form.discount} onChange={handleSimpleChange} readOnly className="w-full bg-custom-background px-4 py-2 rounded-full" />
              </FieldRow>

              {/* Buttons for photo change */}
              <div className="flex gap-4 justify-center mt-4">
                <label className="cursor-pointer flex items-center gap-2 bg-custom-grad1 text-white px-4 py-2 rounded-full hover:opacity-80 transition">
                  <Upload size={18} /> {t.newPhoto}
                  <input type="file" hidden accept="image/*" onChange={(e) => handlePhoto(e.target.files[0])} />
                </label>
                <button type="button" onClick={() => cameraInputRef.current.click()} className="flex items-center gap-2 bg-custom-grad4 text-white px-4 py-2 rounded-full hover:opacity-80 transition">
                  <Camera size={18} /> {t.camera}
                </button>
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => handlePhoto(e.target.files[0])} />
              </div>

              {/* Space for photo preview */}
              {photoPreview && (
                <img src={photoPreview} alt="Preview" className="mx-auto w-48 h-48 object-cover rounded-xl shadow-lg border-2 border-custom-grad1" />
              )}

              {/* Submit button */}
              <div className="flex justify-center mt-6">
                <button onClick={handleSubmit} className="font-bold px-10 py-3 rounded-full shadow-lg transition-all bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 hover:scale-105 text-white">
                  {t.saveBtn}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div role="dialog" aria-modal="true" aria-labelledby="editdiscount-success-title" className="bg-custom-header rounded-2xl p-8 w-80 shadow-2xl text-center border border-custom-grad1">
            <h3 id="editdiscount-success-title" className="text-xl font-bold text-custom-letters mb-2">{t.updated}</h3>
            <p className="text-sm text-custom-letters mb-6">{t.successMsg}</p>
            <button onClick={() => navigate(editSuccessPath)} className="w-full py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-custom-letters font-bold">{t.done}</button>
          </div>
        </div>
      )}
      {showCategoryPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div role="dialog" aria-modal="true" aria-labelledby="editdiscount-cat-title" className="bg-custom-header rounded-2xl p-6 w-full max-w-sm shadow-xl text-center">
            <h3 id="editdiscount-cat-title" className="text-lg font-semibold text-custom-letters mb-4">
              {t.addCatTitle}
            </h3>

            <input
              autoFocus
              className="w-full bg-custom-background px-4 py-3 rounded-full text-sm border-none focus:ring-2 focus:ring-custom-grad1"
              placeholder={language === "el" ? "π.χ. Γαλακτοκομικά" : "e.g. Dairy"}
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
            />

            <div className="flex justify-center gap-3 mt-6">
              <button
                onClick={() => setShowCategoryPopup(false)}
                className="px-4 py-2 text-sm text-custom-letters hover:text-red-500 transition"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleSaveCategory}
                disabled={!newCategoryName}
                className={`px-8 py-2 rounded-full font-bold text-white transition ${newCategoryName ? "bg-gradient-to-r from-custom-grad1 to-custom-grad4" : "bg-gray-300"
                  }`}
              >
                {t.save}
              </button>
            </div>
          </div>
        </div>
      )}

      {errorPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
          <div role="dialog" aria-modal="true" aria-labelledby="editdiscount-error-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
            <h3 id="editdiscount-error-title" className="text-lg font-semibold text-custom-letters mb-3">{errorPopup.title}</h3>
            <p className="text-sm text-custom-letters mb-6">{errorPopup.msg}</p>
            <button
              onClick={() => setErrorPopup(null)}
              className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-custom-letters font-bold">
              {t.ok}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditDiscount;